import type { Timestamp } from "firebase/firestore";

export type UserRole = "customer" | "rider" | "admin";

export interface AppUser {
  uid: string;
  phoneE164: string | null;
  email: string | null;
  role: UserRole;
  displayName: string;
  status: "pending_onboarding" | "active" | "suspended" | "closed";
  createdAt?: Timestamp | null;
  updatedAt?: Timestamp | null;
  schemaVersion: number;
}

export type VehicleType = "boda" | "bicycle" | "car";

export interface Rider {
  uid: string;
  applicationId: string;
  vehicleType: VehicleType;
  plateNumber: string;
  approvalStatus: "approved" | "suspended" | "revoked";
  ratingAverage: number;
  ratingCount: number;
  completedShipmentCount: number;
  createdAt?: Timestamp | null;
  updatedAt?: Timestamp | null;
  schemaVersion: number;
}

export interface RiderApplication {
  id: string;
  phoneE164: string;
  displayName: string;
  vehicleType: VehicleType;
  plateNumber: string;
  documentPaths: Record<string, string>;
  status: "pending_verification" | "verified" | "approved" | "rejected" | "withdrawn";
  linkedUid: string;
  createdByAdminId: string;
  reviewedByAdminId?: string | null;
  rejectionCode?: string | null;
  createdAt?: Timestamp | null;
  verifiedAt?: Timestamp | null;
  reviewedAt?: Timestamp | null;
  updatedAt?: Timestamp | null;
  schemaVersion: number;
}

export type ShipmentStatus =
  | "searching"
  | "offered"
  | "accepted"
  | "en_route_pickup"
  | "arrived_pickup"
  | "picked_up"
  | "in_transit"
  | "arrived_dropoff"
  | "delivered"
  | "cancelled"
  | "expired"
  | "failed";

export interface ShipmentLocation {
  address: string;
  coordinate: { latitude: number; longitude: number };
  landmark?: string;
}

export interface ShipmentPrice {
  subtotalUgx: number;
  discountUgx: number;
  customerTotalUgx: number;
  riderEarningUgx: number;
  platformCommissionUgx: number;
  taxUgx: number;
  surgeBasisPoints: number;
  currency: "UGX";
}

export interface ShipmentPackage {
  size: "small" | "medium" | "large";
  handlingRequirements: string[];
  description?: string | null;
  customerNote?: string | null;
}

export interface Shipment {
  id: string;
  publicCode: string;
  customerId: string;
  assignedRiderId?: string | null;
  status: ShipmentStatus;
  pickup: ShipmentLocation;
  dropoff: ShipmentLocation;
  package: ShipmentPackage;
  price: ShipmentPrice;
  routeDistanceMeters: number;
  routeDurationSeconds: number;
  createdAt?: Timestamp | null;
  updatedAt?: Timestamp | null;
}

export type RiderRecord = RiderApplication & {
  profile: Rider | undefined;
  user: AppUser | undefined;
};

export const ACTIVE_STATUSES: ShipmentStatus[] = [
  "searching",
  "offered",
  "accepted",
  "en_route_pickup",
  "arrived_pickup",
  "picked_up",
  "in_transit",
  "arrived_dropoff",
];

export const COMMISSION_RATE = 0.2;
